import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PageLayout from "@/components/layout/PageLayout";
import { Hero, Section } from "@/components/layout/Sections";
import api, { mediaUrl } from "@/lib/api";
import { Calendar, MapPin, ArrowRight } from "lucide-react";

export default function EventiList() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/events")
      .then((r) => setEvents(r.data || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <PageLayout>
      <Hero
        image="/showroom/moto-hero.webp"
        eyebrow="NSA Motors · Eventi"
        title={<>Eventi<br />NSA Motors</>}
        subtitle={<>Giornate dedicate alla passione per le due ruote.<br />Test ride, raduni, eventi speciali.</>}
      />

      <Section>
        {loading && (
          <p className="text-gray-500 italic">Caricamento eventi…</p>
        )}

        {!loading && events.length === 0 && (
          <div className="text-center py-16 border border-dashed border-gray-300">
            <p className="text-gray-500 text-lg">Nessun evento in programma al momento.</p>
            <p className="text-gray-400 text-sm mt-2">Torna presto per scoprire le nuove date!</p>
          </div>
        )}

        {!loading && events.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {events.map((e, idx) => (
              <motion.div
                key={e.id || e.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: idx * 0.08, ease: [0.16, 1, 0.3, 1] }}
              >
                <Link
                  to={`/moto/eventi/${e.slug}`}
                  className="group block bg-white border border-gray-200 hover:border-[color:var(--nsa-orange)] transition-colors overflow-hidden h-full flex flex-col"
                  data-testid={`event-card-${e.slug}`}
                >
                  <div className="aspect-[4/5] w-full bg-gray-900 overflow-hidden relative">
                    {e.cover_image ? (
                      <img
                        src={mediaUrl(e.cover_image)}
                        alt={e.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                        <span className="font-display uppercase text-white/30 text-2xl tracking-wider px-6 text-center">{e.title}</span>
                      </div>
                    )}
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                      <div className="flex items-center gap-2 text-white">
                        <Calendar size={14} className="text-[color:var(--nsa-orange)]" />
                        <span className="label-eyebrow tracking-widest text-xs">{e.date_label || e.date}</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 flex-1 flex flex-col">
                    <h3 className="font-display uppercase text-lg md:text-xl leading-tight">{e.title}</h3>
                    {e.location && (
                      <p className="mt-2 text-sm text-gray-500 flex items-center gap-1.5">
                        <MapPin size={13} /> {e.location}
                      </p>
                    )}
                    {e.excerpt && (
                      <p className="mt-3 text-gray-600 text-sm leading-relaxed line-clamp-3">{e.excerpt}</p>
                    )}
                    <span className="mt-auto pt-5 inline-flex items-center gap-2 text-[color:var(--nsa-orange)] label-eyebrow text-sm group-hover:gap-3 transition-all">
                      Scopri di più <ArrowRight size={14} />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </Section>
    </PageLayout>
  );
}
